'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, KeyRound, Plus, MoreVertical, Users, Pencil, Power, Mail,
  MailPlus, PenLine, Search, Trash2,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import { api, subir } from '@/lib/api';
import type { Sesion } from '@/lib/auth';
import { ROLES_ELIMINAR_USUARIOS, ROLES_USUARIOS_GESTION_TOTAL } from '@/lib/menu';
import { labelRol } from '@/lib/roles';
import { colorRol, estiloInsigniaRol } from '@/lib/permisos';
import { cn, iniciales } from '@/lib/utils';
import {
  avisarEnlace, fechaHoraTraza, labelArea, rolSinSeccionContrato, textoPlazo,
  type Estado, type RespuestaEnlace, type Usuario,
} from '@/lib/usuarios';
import GestorFirma from '@/components/GestorFirma';
import InputPassword from '@/components/InputPassword';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Usuarios({
  usuario,
  onNavegar,
  onEditar,
}: {
  usuario: Sesion;
  onNavegar?: (v: string) => void;
  onEditar?: (id: string) => void;
}) {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [cargando, setCargando] = useState(true);

  const [modalPass, setModalPass] = useState<Usuario | null>(null);
  const [modalFirma, setModalFirma] = useState<Usuario | null>(null);
  const [modalEliminar, setModalEliminar] = useState<Usuario | null>(null);

  // Quiénes pueden ELIMINAR usuarios (mismo grupo que el backend): directores de
  // instrucción/juzgamiento, superusuario y administrador.
  const puedeEliminar = ROLES_ELIMINAR_USUARIOS.includes(usuario.rol);
  // Gestión total (estado, contraseñas, enlace, firma). El técnico administrativo
  // solo crea y edita (pedido del cliente ago-06): sin el menú ⋮ de la tarjeta.
  const gestionTotal = ROLES_USUARIOS_GESTION_TOTAL.includes(usuario.rol);

  // Rol elegido en el panel de chips (diseño de referencia "Ver usuarios"):
  // sin selección se muestra el estado vacío; con selección, sus usuarios.
  const [rolActivo, setRolActivo] = useState<string | null>(null);
  // Búsqueda DENTRO del rol seleccionado (client-side).
  const [busqueda, setBusqueda] = useState('');

  // Usuarios agrupados por rol → chips del panel superior, en orden alfabético.
  const grupos = useMemo(() => {
    const porRol = new Map<string, Usuario[]>();
    for (const u of usuarios) {
      const lista = porRol.get(u.rol) ?? [];
      lista.push(u);
      porRol.set(u.rol, lista);
    }
    return Array.from(porRol.entries())
      .map(([rol, lista]) => ({ rol, label: labelRol(rol), usuarios: lista }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [usuarios]);

  const grupoActivo = grupos.find((g) => g.rol === rolActivo) ?? null;
  const q = busqueda.trim().toLowerCase();
  const usuariosDelRol = (grupoActivo?.usuarios ?? []).filter((u) =>
    !q || `${u.nombre} ${u.apellido} ${u.numero_documento} ${u.correo}`.toLowerCase().includes(q),
  );

  async function cargar() {
    setCargando(true);
    try {
      const data = await api<Usuario[]>('/usuarios');
      setUsuarios(data);
    } catch (e) {
      avisoError('Error al cargar usuarios', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  async function cambiarEstado(u: Usuario, estado: Estado) {
    try {
      await api(`/usuarios/${u.id}/estado`, { method: 'PATCH', body: { estado } });
      avisoExito(`${u.nombre} ${u.apellido} ahora está ${estado}.`, estado !== 'ACTIVO'
          ? 'Un usuario inactivo/suspendido no podrá realizar actuaciones en el flujo.'
          : undefined);
      cargar();
    } catch (e) {
      avisoError('No se pudo cambiar el estado', e instanceof Error ? e.message : undefined);
    }
  }

  async function reenviarEnlace(u: Usuario) {
    try {
      const r = await api<RespuestaEnlace>(`/usuarios/${u.id}/reenviar-enlace`, { method: 'POST' });
      avisarEnlace(r, `Enlace de contraseña reenviado a ${u.correo}.`);
    } catch (e) {
      avisoError('No se pudo reenviar el enlace', e instanceof Error ? e.message : undefined);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-7 pb-4">
      {/* Encabezado centrado del diseño de referencia ("Gestión de Usuarios"). */}
      <div className="relative text-center">
        <h1 className="text-3xl font-bold tracking-tight text-primary">Gestión de Usuarios</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Seleccione un rol para visualizar y gestionar sus usuarios.
        </p>
        <Button
          className="absolute right-0 top-0 hidden sm:inline-flex"
          onClick={() => onNavegar?.('usuarios-crear')}
        >
          <Plus className="size-4" /> Crear usuario
        </Button>
        {/* En pantallas pequeñas el botón baja del encabezado (no cabe absoluto). */}
        <Button className="mt-3 sm:hidden" onClick={() => onNavegar?.('usuarios-crear')}>
          <Plus className="size-4" /> Crear usuario
        </Button>
      </div>

      {/* Panel de chips por rol (referencia parte 1). */}
      {cargando ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Cargando…</p>
      ) : usuarios.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Sin usuarios.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {grupos.map((g) => {
            const activo = g.rol === rolActivo;
            return (
              <button
                key={g.rol}
                type="button"
                onClick={() => { setRolActivo(activo ? null : g.rol); setBusqueda(''); }}
                className={cn(
                  'flex min-h-16 flex-col items-center justify-center gap-1.5 rounded-xl border px-3 py-4 text-center text-xs font-bold uppercase leading-snug tracking-wide shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md',
                  activo
                    ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/25'
                    : 'border-border bg-gradient-to-b from-card to-muted/50 text-foreground/80 hover:border-primary/40',
                )}
              >
                {/* Punto con el color propio del rol (matriz central). */}
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: colorRol(g.rol) }}
                />
                {g.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Sin rol elegido: estado vacío centrado (referencia parte 1). */}
      {!cargando && usuarios.length > 0 && !grupoActivo && (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="flex size-24 items-center justify-center rounded-full bg-primary/10">
            <Users className="size-10 text-primary" />
          </span>
          <p className="mt-5 text-lg font-semibold">Seleccione un rol</p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            Elija un rol del panel superior para comenzar a gestionar sus usuarios.
          </p>
        </div>
      )}

      {/* Con rol elegido: barra de selección + tarjetas (referencia parte 2). */}
      {grupoActivo && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border bg-card px-5 py-3.5 shadow-sm">
            <span className="size-2 shrink-0 rounded-full bg-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Rol seleccionado:</p>
              <p className="truncate text-lg font-bold uppercase tracking-wide text-primary">
                {grupoActivo.label}
              </p>
            </div>
            <div className="relative hidden md:block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar en este rol…"
                className="h-9 w-52 pl-8"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {usuariosDelRol.length === 1 ? '1 usuario' : `${usuariosDelRol.length} usuarios`}
            </span>
            <Button type="button" variant="outline" onClick={() => { setRolActivo(null); setBusqueda(''); }}>
              Cambiar
            </Button>
          </div>

          <div className="space-y-5">
            {usuariosDelRol.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No hay coincidencias con la búsqueda en este rol.
              </p>
            ) : (
              usuariosDelRol.map((u) => (
                <TarjetaUsuario
                  key={u.id}
                  usuario={u}
                  conGestionTotal={gestionTotal}
                  onEditar={() => onEditar?.(u.id)}
                  // Nadie se elimina a sí mismo (el backend también lo bloquea).
                  onEliminar={puedeEliminar && u.id !== usuario.id ? () => setModalEliminar(u) : undefined}
                  onEstado={() => cambiarEstado(u, u.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO')}
                  onReenviar={() => reenviarEnlace(u)}
                  onPassword={() => setModalPass(u)}
                  onFirma={() => setModalFirma(u)}
                />
              ))
            )}
          </div>
        </>
      )}

      {modalEliminar && (
        <ModalEliminar
          usuario={modalEliminar}
          // Para reasignar: otros usuarios ACTIVOS del MISMO rol (regla del negocio).
          candidatos={usuarios.filter(
            (c) => c.rol === modalEliminar.rol && c.id !== modalEliminar.id && c.estado === 'ACTIVO',
          )}
          onClose={() => setModalEliminar(null)}
          onListo={(reasignados) => {
            setModalEliminar(null);
            avisoExito(`Usuario ${modalEliminar.nombre} ${modalEliminar.apellido} eliminado.`, reasignados > 0
                ? `${reasignados} proceso(s) reasignados antes de eliminar.`
                : 'No tenía procesos pendientes.');
            cargar();
          }}
        />
      )}

      {modalPass && (
        <ModalPassword
          usuario={modalPass}
          onClose={() => setModalPass(null)}
          onListo={(msg) => { setModalPass(null); avisoExito(msg); }}
        />
      )}

      {modalFirma && (
        <ModalFirma
          usuario={modalFirma}
          onClose={() => setModalFirma(null)}
          onCambio={cargar}
        />
      )}
    </div>
  );
}

// ---- Tarjeta de usuario (diseño de referencia "Ver usuarios Parte 2") ------
// Banda lateral en degradado con avatar + estado, cuerpo con campos etiquetados
// en dos columnas y botonera vertical de acciones a la derecha: lápiz (editar),
// basura (eliminar) y ⋮ (activar/desactivar, enlace, contraseña, firma).

const fechaCorta = (iso?: string | null) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : null;

const ESTADO_PILL: Record<string, { dot: string; label: string }> = {
  ACTIVO: { dot: 'bg-emerald-500', label: 'Activo' },
  INACTIVO: { dot: 'bg-slate-400', label: 'Inactivo' },
  SUSPENDIDO: { dot: 'bg-red-500', label: 'Suspendido' },
};

function TarjetaUsuario({
  usuario: u, conGestionTotal, onEditar, onEliminar, onEstado, onReenviar, onPassword, onFirma,
}: {
  usuario: Usuario;
  // Sin gestión total (técnico administrativo) la tarjeta solo ofrece Editar.
  conGestionTotal: boolean;
  onEditar: () => void;
  onEliminar?: () => void;
  onEstado: () => void;
  onReenviar: () => void;
  onPassword: () => void;
  onFirma: () => void;
}) {
  const pill = ESTADO_PILL[u.estado] ?? ESTADO_PILL.INACTIVO;
  // Funcionario de planta (o el viejo contrato 'N/A'): no aplica contrato.
  const sinContrato = u.es_funcionario === true
    || !u.numero_contrato || u.numero_contrato.trim().toUpperCase() === 'N/A';
  const finContrato = u.tiene_adicion && u.fecha_terminacion_adicion
    ? u.fecha_terminacion_adicion
    : u.fecha_terminacion_contrato;
  // Plazo digitado (manual): del contrato y, si la hay, de la adición.
  const plazo = textoPlazo(u.plazo_meses ?? 0, u.plazo_dias ?? 0);
  const plazoAdicion = u.tiene_adicion
    ? textoPlazo(u.adicion_meses ?? 0, u.adicion_dias ?? 0)
    : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm sm:flex-row">
      {/* Banda con avatar + estado: teñida con el color propio del rol (matriz central). */}
      <div
        className="flex shrink-0 items-center justify-center gap-3 p-3 sm:w-32 sm:flex-col sm:gap-2.5 sm:p-4"
        style={{ background: `linear-gradient(to bottom, ${colorRol(u.rol)}, ${colorRol(u.rol)}cc)` }}
      >
        <Avatar className="size-12 ring-4 ring-white/25 sm:size-16">
          <AvatarFallback className="bg-white text-base font-bold sm:text-lg" style={{ color: colorRol(u.rol) }}>
            {iniciales(`${u.nombre} ${u.apellido}`)}
          </AvatarFallback>
        </Avatar>
        <span className="flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-semibold text-slate-800">
          <span className={cn('size-1.5 shrink-0 rounded-full', pill.dot)} />
          {pill.label}
        </span>
      </div>

      {/* Cuerpo: nombre + badge de rol + campos etiquetados. */}
      <div className="min-w-0 flex-1 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-bold uppercase leading-tight tracking-tight text-primary">
            {u.nombre} {u.apellido}
          </h3>
          <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-wide" style={estiloInsigniaRol(u.rol)}>
            {labelRol(u.rol)}
          </Badge>
        </div>

        <dl className="mt-4 grid gap-x-10 gap-y-3 sm:grid-cols-2">
          <Dato label="Documento">{u.numero_documento}</Dato>
          <Dato label="Tipo de documento">{u.tipo_documento}</Dato>
          <Dato label="Teléfono">{u.telefono || '—'}</Dato>
          <Dato label="Correo electrónico">
            <a
              href={`mailto:${u.correo}`}
              className="inline-flex max-w-full items-center gap-1.5 font-semibold text-primary hover:underline"
            >
              <Mail className="size-3.5 shrink-0" />
              <span className="truncate">{u.correo}</span>
            </a>
          </Dato>
          <Dato label="Área / Dependencia">{labelArea(u.area)}</Dato>
          <Dato label="Contrato">
            {sinContrato ? (
              // A supervisor/contratista no se les captura el contrato en el alta.
              rolSinSeccionContrato(u.rol) ? 'No se registra' : 'Funcionario (no aplica contrato)'
            ) : (
              <>
                N.º {u.numero_contrato}
                {fechaCorta(u.fecha_inicio_contrato) && (
                  <span className="block">
                    <span className="text-primary">{fechaCorta(u.fecha_inicio_contrato)}</span>
                    <span className="font-normal text-muted-foreground"> a </span>
                    <span className="text-primary">{fechaCorta(finContrato)}</span>
                    {u.tiene_adicion && u.fecha_terminacion_adicion && (
                      <span className="font-normal text-muted-foreground"> (con adición)</span>
                    )}
                  </span>
                )}
                {plazo && (
                  <span className="block font-normal text-muted-foreground">
                    Plazo: {plazo}
                    {plazoAdicion && ` + adición ${plazoAdicion}`}
                  </span>
                )}
              </>
            )}
          </Dato>
        </dl>

        {/* Trazabilidad del registro: quién lo creó y quién lo editó (con hora). */}
        <p className="mt-3 border-t pt-2 text-xs text-muted-foreground">
          Creado por <span className="font-medium text-foreground">{u.creado_por_nombre ?? '—'}</span>
          {' · '}{fechaHoraTraza(u.fecha_creacion)}
          {u.modificado_por_nombre && (
            <>
              {' · Editado por '}
              <span className="font-medium text-foreground">{u.modificado_por_nombre}</span>
              {' · '}{fechaHoraTraza(u.fecha_modificacion)}
            </>
          )}
        </p>
      </div>

      {/* Botonera de acciones: fila inferior en móvil, columna lateral en amplio. */}
      <div className="flex shrink-0 items-center justify-center gap-2 border-t bg-muted/20 p-2.5 sm:flex-col sm:border-l sm:border-t-0 sm:px-3">
        <Button
          size="icon" onClick={onEditar} className="rounded-lg shadow-sm"
          aria-label="Editar usuario" title="Editar usuario"
        >
          <Pencil className="size-4" />
        </Button>
        {onEliminar && (
          <Button
            size="icon" variant="outline" onClick={onEliminar}
            className="rounded-lg text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive"
            aria-label="Eliminar usuario" title="Eliminar usuario"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
        {conGestionTotal && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" className="rounded-lg shadow-sm" aria-label="Más acciones">
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Activar/Inactivar vive aquí (antes era el botón Power de la tarjeta). */}
            <DropdownMenuItem
              onClick={onEstado}
              className={u.estado === 'ACTIVO' ? 'text-amber-600' : 'text-emerald-600'}
            >
              <Power className="size-4" />
              {u.estado === 'ACTIVO' ? 'Inactivar usuario' : 'Activar usuario'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onReenviar}>
              <MailPlus className="size-4" /> Reenviar enlace de contraseña
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPassword}>
              <KeyRound className="size-4" /> Fijar contraseña manual
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onFirma}>
              <PenLine className="size-4" /> Cargar firma
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>
    </div>
  );
}

// Campo etiquetado de la tarjeta: rótulo pequeño gris + valor en seminegrita.
function Dato({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-foreground">{children}</dd>
    </div>
  );
}

// ---- Modal: eliminar usuario (borrado lógico, con reasignación) -------------
// Consulta primero los procesos SIN FINALIZAR asignados al usuario: si no tiene,
// se elimina directo; si tiene, obliga a reasignarlos a otro usuario ACTIVO del
// MISMO rol (regla del cliente) antes de eliminar. El borrado es lógico: el
// usuario desaparece de las listas y no puede volver a ingresar.
interface ProcesoPendiente {
  tipo: string;
  etiqueta: string;
  referencia: string;
  detalle?: string;
}

function ModalEliminar({
  usuario: u, candidatos, onClose, onListo,
}: {
  usuario: Usuario;
  candidatos: Usuario[];
  onClose: () => void;
  onListo: (reasignados: number) => void;
}) {
  // null = consultando los procesos del usuario.
  const [procesos, setProcesos] = useState<ProcesoPendiente[] | null>(null);
  const [destino, setDestino] = useState('');
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => {
    api<{ total: number; procesos: ProcesoPendiente[] }>(`/usuarios/${u.id}/procesos`)
      .then((r) => setProcesos(r.procesos))
      .catch((e) => {
        avisoError('No se pudieron consultar los procesos del usuario', e instanceof Error ? e.message : undefined);
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conProcesos = (procesos?.length ?? 0) > 0;
  const sinCandidatos = conProcesos && candidatos.length === 0;

  async function eliminar() {
    setEliminando(true);
    try {
      const r = await api<{ ok: boolean; reasignados: number }>(`/usuarios/${u.id}`, {
        method: 'DELETE',
        ...(conProcesos ? { body: { reasignarA: destino } } : {}),
      });
      onListo(r.reasignados);
    } catch (e) {
      avisoError('No se pudo eliminar el usuario', e instanceof Error ? e.message : undefined);
      setEliminando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="size-5" /> Eliminar usuario
          </DialogTitle>
          <DialogDescription>
            <strong>{u.nombre} {u.apellido}</strong> ({u.numero_documento}) — {labelRol(u.rol)}.
            El usuario no podrá volver a ingresar y desaparecerá de las listas; su
            historial queda conservado para auditoría.
          </DialogDescription>
        </DialogHeader>

        {procesos === null ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Verificando procesos sin finalizar…
          </p>
        ) : !conProcesos ? (
          <p className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
            El usuario <strong>no tiene procesos sin finalizar</strong>: se puede eliminar
            directamente.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <p>
                Tiene <strong>{procesos.length} proceso(s) sin finalizar</strong>. Para no
                perderlos, debe reasignarlos a otro usuario <strong>activo del mismo rol</strong> antes
                de eliminar.
              </p>
            </div>

            <ul className="max-h-44 space-y-1.5 overflow-y-auto rounded-lg border p-3 text-sm">
              {procesos.map((p, i) => (
                <li key={`${p.tipo}-${p.referencia}-${i}`} className="flex items-baseline gap-2">
                  <span className="font-semibold">{p.referencia}</span>
                  <span className="text-muted-foreground">
                    {p.etiqueta}{p.detalle ? ` · ${p.detalle}` : ''}
                  </span>
                </li>
              ))}
            </ul>

            {sinCandidatos ? (
              <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                No hay otro usuario ACTIVO con el rol {labelRol(u.rol)} para recibir los
                procesos. Cree o active uno antes de eliminar a este usuario.
              </p>
            ) : (
              <div className="space-y-2">
                <Label>Reasignar los procesos a</Label>
                <Select value={destino} onValueChange={setDestino}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={`Seleccione un usuario (${labelRol(u.rol)})`} />
                  </SelectTrigger>
                  <SelectContent>
                    {candidatos.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} {c.apellido} ({c.numero_documento})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            type="button"
            variant="destructive"
            disabled={procesos === null || eliminando || sinCandidatos || (conProcesos && !destino)}
            onClick={eliminar}
          >
            {eliminando
              ? 'Eliminando…'
              : conProcesos ? 'Reasignar y eliminar' : 'Eliminar usuario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: firma del usuario (vista previa + reemplazar + eliminar) -------
function ModalFirma({ usuario, onClose, onCambio }: { usuario: Usuario; onClose: () => void; onCambio: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="size-5 text-primary" /> Firma del usuario
          </DialogTitle>
          <DialogDescription>
            Firma de <strong>{usuario.nombre} {usuario.apellido}</strong> (PNG/JPG). Se estampará en el acta de reparto y otros documentos.
          </DialogDescription>
        </DialogHeader>
        <GestorFirma
          firmaKeyInicial={usuario.firma_key ?? null}
          onSubir={async (file) => {
            const fd = new FormData();
            fd.append('file', file);
            const u = await subir<{ firma_key?: string | null }>(`/usuarios/${usuario.id}/firma`, fd);
            return u.firma_key ?? null;
          }}
          onEliminar={async () => { await api(`/usuarios/${usuario.id}/firma`, { method: 'DELETE' }); }}
          onCambio={onCambio}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: fijar contraseña manual (admin) -------------------------------
function ModalPassword({ usuario, onClose, onListo }: { usuario: Usuario; onClose: () => void; onListo: (msg: string) => void }) {
  const [nueva, setNueva] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await api(`/usuarios/${usuario.id}/password`, { method: 'PATCH', body: { nuevaPassword: nueva } });
      onListo(`Contraseña de ${usuario.nombre} ${usuario.apellido} actualizada.`);
    } catch (e) {
      avisoError('No se pudo cambiar la contraseña', e instanceof Error ? e.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-5 text-primary" /> Fijar contraseña manual
          </DialogTitle>
          <DialogDescription>
            Usuario: <strong>{usuario.nombre} {usuario.apellido}</strong> ({usuario.numero_documento})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nueva">Nueva contraseña</Label>
            <InputPassword id="nueva" autoComplete="new-password" value={nueva} onChange={(e) => setNueva(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
